import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { abortActiveOfflineDownloads, syncOfflineServiceWorker } from '../../services/offlineService';
import { useAuth } from '../context/AuthContext';
import SessionAlert from '../components/SessionAlert';
import SessionLockScreen from '../components/SessionLockScreen';
import AppSnackbar from '../components/AppSnackbar';
// sessionStorage key -  per-tab, survives page reloads but not new tabs or sign-out.
// Value is the locked user's email so it can be displayed immediately on reload.
const LOCK_KEY = 'session_locked';

function readStoredLock() {
  try { return sessionStorage.getItem(LOCK_KEY) ?? ''; } catch { return ''; }
}

/** @param {string} email */
function writeStoredLock(email) {
  try { sessionStorage.setItem(LOCK_KEY, email || '1'); } catch {}
}

function clearStoredLock() {
  try { sessionStorage.removeItem(LOCK_KEY); } catch {}
}

/**
 * @param {{
 *   timeoutMinutes?: number;
 *   warningOffsetMinutes?: number;
 * }} props
 */
export function SessionTimeoutGuard(props) {
  const {
    timeoutMinutes = 15,
    warningOffsetMinutes = 1,
  } = props;

  const { user, isAuthResolved, signOut } = useAuth();

  const storedLockEmail = readStoredLock();

  const location = useLocation();

  const [isShowingWarning, setIsShowingWarning] = useState(false);
  const [evictionNotice, setEvictionNotice] = useState(
    /** @type {{ label: string; count: number; maxDevices: number } | null} */
    (location.state?.evictionNotice ?? null)
  );
  // Initialize from sessionStorage so lock survives address-bar navigation and page reload.
  const [isLocked, setIsLocked] = useState(Boolean(storedLockEmail));
  const [lockEmail, setLockEmail] = useState(storedLockEmail);

  const isSignedInRef = useRef(false);
  // Ref mirror of isLocked so resetTimer can read the current value without a stale closure.
  const isLockedRef = useRef(Boolean(storedLockEmail));

  const timerRef = useRef(/** @type {number | undefined} */ (undefined));
  const warningRef = useRef(/** @type {number | undefined} */ (undefined));

  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.evictionNotice) {
      navigate(location.pathname + location.search, {
        replace: true,
        state: { ...location.state, evictionNotice: undefined },
      });
    }
  }, []); // intentionally runs once on mount to clear stale router state

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningOffsetMs = warningOffsetMinutes * 60 * 1000;
  const expiryMessage = warningOffsetMinutes >= 1
    ? `Session locks in ${warningOffsetMinutes} minute${warningOffsetMinutes === 1 ? '' : 's'}`
    : `Session locks in ${Math.round(warningOffsetMinutes * 60)} seconds`;

  // Keep ref in sync with state so callbacks always see the latest value.
  useEffect(() => { isLockedRef.current = isLocked; }, [isLocked]);

  const clearIdleTimers = useCallback(() => {
    clearTimeout(timerRef.current);
    clearTimeout(warningRef.current);
  }, []);

  const handleSignOut = useCallback(async () => {
    setIsShowingWarning(false);
    clearIdleTimers();
    clearStoredLock();
    try { await abortActiveOfflineDownloads(); } catch (err) {
      console.warn('Failed to abort active offline downloads on timeout logout:', err);
    }
    try { await signOut(); } catch (err) { /* ignore */ }
    try { await syncOfflineServiceWorker(false); } catch (err) {
      console.warn('Failed to unregister offline service worker on timeout logout:', err);
    }
    navigate('/login');
  }, [clearIdleTimers, navigate, signOut]);

  const handleSoftLock = useCallback(() => {
    setIsShowingWarning(false);
    clearIdleTimers();
    const email = user?.email ?? '';
    writeStoredLock(email);
    isLockedRef.current = true;
    setLockEmail(email);
    setIsLocked(true);
  }, [user, clearIdleTimers]);

  const resetTimer = useCallback(() => {
    // Do not reset timers when locked — the session is locked, activity is irrelevant.
    if (!isSignedInRef.current || isLockedRef.current) {
      clearIdleTimers();
      setIsShowingWarning(false);
      return;
    }
    clearIdleTimers();
    warningRef.current = window.setTimeout(() => {
      setIsShowingWarning(true);
    }, Math.max(timeoutMs - warningOffsetMs, 0));
    timerRef.current = window.setTimeout(() => {
      handleSoftLock();
    }, timeoutMs);
  }, [clearIdleTimers, handleSoftLock, timeoutMs, warningOffsetMs]);

  const handleExtendSession = useCallback(() => {
    if (!isSignedInRef.current) return;
    setIsShowingWarning(false);
    resetTimer();
  }, [resetTimer]);

  const handleUnlocked = useCallback(() => {
    clearStoredLock();
    isLockedRef.current = false;
    setIsLocked(false);
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!isAuthResolved) return;
    if (user) {
      isSignedInRef.current = true;
      // If we loaded into a locked state from sessionStorage, populate email from auth
      // in case the stored value was stale or empty (e.g. first lock before email was set).
      if (isLockedRef.current) {
        setLockEmail((prev) => prev || user.email || '');
      }
      resetTimer();
    } else {
      isSignedInRef.current = false;
      clearStoredLock();
      isLockedRef.current = false;
      setIsLocked(false);
      setIsShowingWarning(false);
      clearIdleTimers();
    }
  }, [isAuthResolved, user, clearIdleTimers, resetTimer]);

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    window.addEventListener('extend-session', resetTimer);
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      window.removeEventListener('extend-session', resetTimer);
    };
  }, [resetTimer]);

  return (
    <>
      <AppSnackbar
        open={!!evictionNotice}
        message={evictionNotice
          ? evictionNotice.count > 1
            ? `${evictionNotice.count} trusted devices were removed to stay within your device limit of ${evictionNotice.maxDevices}.`
            : `${evictionNotice.label} has been removed from your trusted devices. The maximum number of trusted devices allowed is ${evictionNotice.maxDevices}.`
          : ''}
        onClose={() => setEvictionNotice(null)}
      />
      <SessionAlert
        open={isShowingWarning}
        onClose={() => setIsShowingWarning(false)}
        onExtend={handleExtendSession}
        onSignOut={handleSignOut}
        message="Your session will lock soon due to inactivity. You'll need to re‑enter your password to continue."
        expiryMessage={expiryMessage}
      />
      <SessionLockScreen
        open={isLocked}
        userEmail={lockEmail}
        onUnlocked={handleUnlocked}
        onSignOut={handleSignOut}
      />
      {!isLocked && <Outlet />}
    </>
  );
}
